import { useState, useRef, useEffect, useContext } from "react";
import { Hub } from "aws-amplify";
import { CONNECTION_STATE_CHANGE, ConnectionState } from "@aws-amplify/pubsub";
import { Link } from "react-router-dom";
import { UserPlus, X } from "lucide-react";

import api from "apiSingleton";
import { AuthContext, JobsContext } from "context/providers";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "components/ui/dialog";

import { USER_STATUS } from "lookup";
import { cn } from "utils";
import { getPlatformProfileUrl } from "utils/helpers/users";

import { ResumeUploader } from "../ResumeUploader";

const getRandomSuffix = () => {
  return new Array(4)
    .fill(9)
    .map((num) => Math.round(Math.random() * num))
    .join("");
};

export const TalentCreation = ({
  jobOpportunityId,
  userMatched,
  isModalView = false,
}) => {
  const { user } = useContext(AuthContext);
  const [email, setEmail] = useState("");
  const [userExists, setUserExists] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState({
    status: null,
    message: null,
  });
  const [createdUser, setCreatedUser] = useState(null);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [profileUrl, setProfileUrl] = useState(null);

  const identityUsername = useRef(null);
  const archivedUserRef = useRef(null);
  const lastConnectionStateRef = useRef(null);
  const subscriptionRef = useRef(null);
  const emailRef = useRef("");
  const intervalRef = useRef(null);
  const intervalTriggeredRef = useRef(false);
  const connectionStatusTimeoutRef = useRef();
  const notificationMessageRef = useRef({
    status: null,
    message: null,
  });

  const connectSubs = async (identityUsername) => {
    subscriptionRef.current = await getSubscription(identityUsername);

    if (!subscriptionRef.current) {
      subscriptionRef.current = null;

      setNotificationMessage({
        type: "error",
        message: "failed to connect to notification server",
      });
    }
  };

  useEffect(() => {
    Hub.listen("api", async (data) => {
      const { payload } = data;

      if (payload.event === CONNECTION_STATE_CHANGE) {
        const connectionState = payload.data.connectionState;

        if (
          connectionState === ConnectionState.Connected &&
          connectionStatusTimeoutRef.current
        ) {
          clearTimeout(connectionStatusTimeoutRef.current);
        }

        if (
          identityUsername.current &&
          connectionState === ConnectionState.Disconnected
        ) {
          await connectSubs(identityUsername.current);
        }

        if (
          lastConnectionStateRef.current === ConnectionState.Connected &&
          connectionState !== ConnectionState.Connected
        ) {
          connectionStatusTimeoutRef.current = setTimeout(() => {
            setNotificationMessage({
              status: "info",
              message:
                "Connection lost.\nPlease check your network connection.",
            });
          }, 10000);
        }

        if (
          lastConnectionStateRef.current !== ConnectionState.Connected &&
          (connectionState === ConnectionState.Connected ||
            connectionState === ConnectionState.ConnectedPendingKeepAlive) &&
          intervalTriggeredRef.current &&
          notificationMessageRef.current.status !==
            "resume_parse_process_successful"
        ) {
          intervalTriggeredRef.current = false;

          await userCreatedCheck(
            emailRef.current,
            getPlatformProfileUrl(identityUsername.current)
          );
        }

        lastConnectionStateRef.current = connectionState;
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkUserExistsORIsArchived = async (email) => {
    const { data } = await api.user.getByEmail({ email, limit: 1 });

    const user = data?.getUserByEmail?.items?.[0];

    if (user && user.status === USER_STATUS.ARCHIVED) {
      archivedUserRef.current = user;
    }

    return user ? user?.status !== USER_STATUS.ARCHIVED : false;
  };

  const userCreatedCheck = async (email, profileUrl) => {
    let userExists;

    try {
      userExists = await checkUserExistsORIsArchived(email);
    } catch (err) {
      console.log("Something went wrong", err);
    }

    if (userExists) {
      setCreatedUser(true);

      setProfileUrl(profileUrl);

      setNotificationMessage({
        status: "info",
        message: "The user has been successfully created.",
      });

      return userExists;
    }
  };

  const emailIsAvailable = async () => {
    setIsCheckingEmail(true);

    const isAvailable = await checkUserExistsORIsArchived(email);

    setUserExists(isAvailable);
    setShowUpload(!isAvailable);
    setIsCheckingEmail(false);
  };

  const onEmailChange = (e) => {
    if (subscriptionRef.current) {
      return;
    }

    const nextEmail = e.target.value.toLowerCase();

    // Storing in ref so value will be present in the Hub listener
    emailRef.current = nextEmail;

    setEmail(nextEmail);
    setUserExists(false);
    setIsCheckingEmail(false);
    setNotificationMessage({
      status: null,
      message: null,
    });
    setProfileUrl(null);
  };

  const getSubscription = async (identityUsername) => {
    const notificationSubscription = await api.user.onUserResumeDataExtraction({
      identity_username: identityUsername,
    });

    if (!notificationSubscription) {
      return;
    }

    return notificationSubscription.subscribe({
      next: ({ value }) => {
        const message = value?.data?.onUserResumeDataExtraction?.data;

        try {
          const data = JSON.parse(message);

          setNotificationMessage(data);

          if (data.status === "resume_parse_process_successful") {
            setCreatedUser(true);
          }
          if (data.status === "user_created") {
            setProfileUrl(getPlatformProfileUrl(identityUsername));
          }

          if (data.status === "match_created" && userMatched) {
            userMatched.current = true;
          }
        } catch (e) {
          clearInterval(intervalRef.current);

          setNotificationMessage({
            status: "error",
            message:
              "Error happened while grabbing notification. See console for error",
          });
          console.log(`Notification failed: ${value}`);
          console.error(e);
        }
      },
      error: (error) => {
        console.log("subscribe error", error);

        subscriptionRef.current = null;

        setNotificationMessage({
          type: "error",
          message: "Failed to connect to notification server",
        });
      },
    });
  };

  const prepareTalentResumeUpload = async () => {
    if (!email.match(/^[\w-.+]+@([\w-]+\.)+[\w-]{2,}$/g)) {
      return null;
    }

    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
    }

    identityUsername.current =
      archivedUserRef.current?.identity_username ||
      `rm_${email
        .split("@")[0]
        .replaceAll(/[^a-zA-Z0-9]/g, "")}${getRandomSuffix()}`;

    archivedUserRef.current = null;

    const context = {
      email,
      creatorId: user.id,
      identity_username: identityUsername.current,
      origin: window.location.origin,
      needsPriorityProcessing: true,
    };

    if (jobOpportunityId) {
      context.jobOpportunityId = jobOpportunityId;
    }

    await connectSubs(identityUsername.current);

    return { publicId: `${identityUsername.current}_resume`, context };
  };

  const handleResumeUploadSuccess = () => {
    setShowUpload(false);

    clearInterval(intervalRef.current);

    intervalRef.current = setInterval(async () => {
      intervalTriggeredRef.current = true;

      const userIsCreated = await userCreatedCheck(
        emailRef.current,
        getPlatformProfileUrl(identityUsername.current)
      );

      if (userIsCreated) {
        clearInterval(intervalRef.current);

        return;
      }

      setNotificationMessage({
        status: "info",
        message:
          "Process is taking longer than expected, please check your network connection.",
      });
    }, 1000 * 60 * 3.5); // 3.5 minutes interval
  };

  const handleNewTalent = () => {
    clearInterval(intervalRef.current);

    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
      subscriptionRef.current = null;
    }

    identityUsername.current = null;
    archivedUserRef.current = null;
    intervalTriggeredRef.current = false;
    lastConnectionStateRef.current = null;
    emailRef.current = "";

    setEmail("");
    setUserExists(false);
    setIsCheckingEmail(false);
    setShowUpload(false);
    setNotificationMessage({
      status: null,
      message: null,
    });
    setCreatedUser(null);
    setProfileUrl(null);
  };

  useEffect(() => {
    // Storing in ref so value will be present in the Hub listener
    notificationMessageRef.current = { ...notificationMessage };
  }, [notificationMessage]);

  useEffect(() => {
    if (email.match(/^[\w-.+]+@([\w-]+\.)+[\w-]{2,}$/g)) {
      archivedUserRef.current = null;

      const timeout = setTimeout(() => emailIsAvailable(), 700);

      return () => {
        clearTimeout(timeout);
        setIsCheckingEmail(false);
      };
    } else {
      setIsCheckingEmail(false);
      setUserExists(false);
      setShowUpload(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  useEffect(() => {
    clearInterval(intervalRef.current);

    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
      subscriptionRef.current = null;

      setNotificationMessage((prev) => ({
        ...prev,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdUser]);

  useEffect(() => {
    return () => {
      clearInterval(intervalRef.current);

      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showProfileUrl =
    !!profileUrl &&
    (user?.attributes?.groups?.includes(
      process.env.REACT_APP_COGNITO_ADMIN_GROUP
    ) ||
      user?.attributes?.groups?.includes(
        process.env.REACT_APP_COGNITO_GROUP_JOB_MANAGERS
      ));
  const isProcessingResume = subscriptionRef.current && !showUpload;
  const showCompactResumeState = isProcessingResume || createdUser;

  return (
    <section
      className={cn("flex justify-center px-6 py-4", !isModalView && "my-20")}
    >
      <div className="max-w-[583px] flex flex-col gap-6 w-full">
        {!isModalView && (
          <h2 className="text-xl font-semibold text-[#334155]">
            Talent Creation
          </h2>
        )}
        {!showCompactResumeState && (
          <>
            <p className="font-['Inter'] text-[16px] font-normal leading-[1.5] tracking-[-0.01em] text-[#334155]">
              Please enter the talent's email below to see whether there is
              already a profile in the TORC platform.
            </p>
            <div className="flex flex-col gap-1">
              <p
                htmlFor="email-input"
                className="font-['Inter'] text-[14px] font-medium leading-[24px] tracking-normal text-[#334155]"
              >
                E-mail
              </p>
              <div className="relative">
                <input
                  id="email-input"
                  type="text"
                  className="w-full rounded-md border-2 border-[#CBD5E1] px-3 py-2 pr-10"
                  placeholder="Enter talent's email"
                  value={email}
                  onChange={onEmailChange}
                  disabled={subscriptionRef.current || createdUser}
                />
                {isCheckingEmail && (
                  <span className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center justify-center">
                    <span className="loader !h-4 !w-4 !border-2" />
                  </span>
                )}
              </div>
              {userExists && (
                <p className="text-red-400">Email already in use</p>
              )}
            </div>
            {showUpload && !createdUser && (
              <div className="flex flex-col gap-4">
                <p className="font-['Inter'] text-[14px] font-medium not-italic leading-[24px] tracking-normal text-[#334155]">
                  Upload the resume
                </p>
                <p className="font-['Inter'] font-normal text-sm leading-[150%] tracking-[-0.01em] align-middle text-[#6B7280]">
                  Upload the Talent's resume to automatically fill in their
                  profile details
                  {jobOpportunityId ? " and match them to this job" : ""}.
                  They'll be able to review and complete any additional
                  information afterward.
                </p>
                <ResumeUploader
                  key={email}
                  onPrepareUpload={prepareTalentResumeUpload}
                  onUploadSuccess={handleResumeUploadSuccess}
                />
              </div>
            )}
          </>
        )}

        {showCompactResumeState && (
          <>
            <div className="flex flex-col min-h-[215px] justify-center items-center gap-6">
              {notificationMessage.message && (
                <div className="font-['Inter'] font-medium text-sm leading-6 tracking-normal text-center text-[#334155]">
                  {notificationMessage.message}
                </div>
              )}
              {isProcessingResume && <span className="loader"></span>}

              {createdUser && (
                <a
                  className="text-sm text-blue hover:underline"
                  href={profileUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {profileUrl}
                </a>
              )}
            </div>
            <div className="flex flex-col items-end gap-3">
              {createdUser && (
                <div className="flex justify-end gap-3">
                  {!isModalView && (
                    <button
                      type="button"
                      className="inline-flex h-10 items-center justify-center rounded-md border border-[#CBD5E1] bg-white px-4 py-2 text-sm font-medium text-[#334155] transition-colors hover:bg-slate-50"
                      onClick={handleNewTalent}
                    >
                      New talent
                    </button>
                  )}
                  <Link
                    className="inline-flex h-10 items-center justify-center rounded-md border border-[#CBD5E1] bg-white px-4 py-2 text-sm font-medium text-[#334155] transition-colors hover:bg-slate-50"
                    to={{
                      pathname: "/usermanager",
                      search: `?search=${encodeURIComponent(email)}`,
                    }}
                    target="_blank"
                    rel="noreferrer"
                  >
                    View {email}'s profile
                  </Link>
                </div>
              )}
            </div>
          </>
        )}
        {!showCompactResumeState && notificationMessage.message && (
          <div className="flex justify-center mt-8 whitespace-pre-wrap">
            {notificationMessage.message}
          </div>
        )}
        {!showCompactResumeState && showProfileUrl && (
          <div className="mt-2 flex justify-center">
            <a
              className="text-blue hover:underline"
              href={profileUrl}
              target="_blank"
              rel="noreferrer"
            >
              {email}'s Profile
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

const TalentCreationModal = ({ jobOpportunityId, className, tooltip }) => {
  const { jobOpp, clearJob, initJob } = useContext(JobsContext);
  const [open, setOpen] = useState(false);
  const userMatched = useRef();

  const handleClose = (matched) => {
    if (matched && jobOpp && jobOpp.id) {
      clearJob(jobOpp.id);
      initJob(jobOpp.id);
    }

    setOpen(false);
  };

  const handleOpenChange = (nextOpen) => {
    if (nextOpen) {
      setOpen(true);
      return;
    }

    handleClose(userMatched.current);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-slate-700 ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            className
          )}
          title={tooltip}
        >
          <UserPlus size={16} />
          <span>Talent creation</span>
        </button>
      </DialogTrigger>

      <DialogContent
        className={cn(
          "max-h-[95vh] max-w-[583px] overflow-y-auto p-0 [&>button.absolute]:hidden"
        )}
      >
        <DialogHeader className="flex shrink-0 flex-row items-center gap-4 space-y-0 border-b border-slate-100 px-6 py-4 text-left sm:text-left">
          <div className="flex min-w-0 flex-1 items-center justify-between gap-4">
            <DialogTitle className="font-['Inter'] text-xl font-semibold text-slate-700">
              Talent Creation
            </DialogTitle>
          </div>
          <button
            type="button"
            className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-gray-500 text-white transition hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2"
            aria-label="Close"
            onClick={() => handleOpenChange(false)}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        </DialogHeader>

        <TalentCreation
          jobOpportunityId={jobOpportunityId}
          userMatched={userMatched}
          isModalView
        />
      </DialogContent>
    </Dialog>
  );
};

export default TalentCreationModal;
